// /src/utils/timeFormatter.ts

type FormatTimeOptions = {
  /** Defaults to "en-US" */
  locale?: string;
  /**
   * Optional IANA timezone, e.g. "UTC" or "America/New_York".
   * If omitted, the user's local timezone is used.
   */
  timeZone?: string;
};

/**
 * Converts an ISO timestamp like "2026-02-05T19:01:04.946439Z" to "10:45AM".
 */
export function formatIsoToTime12h(isoString: string, opts: FormatTimeOptions = {}): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  const { locale = 'en-US', timeZone } = opts;

  const formatted = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }).format(date);

  // "10:45 AM" -> "10:45AM"
  return formatted.replace(/\s+/g, '');
}

/**
 * Formats an ISO timestamp to a human-readable string in local timezone.
 * Example: "2026-02-05T19:01:04.946439Z" -> "Feb 5, 7:01 PM" (local)
 */
export function formatIsoTimestamp(iso: string): string {
  const date = new Date(iso);

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return formatter.format(date);
}

/**
 * Formats an ISO timestamp to MM/DD/YYYY format.
 * Example: "2026-02-14T19:01:04.946439Z" -> "02/14/2026"
 */
export function formatIsoToDateSlash(isoString: string, opts: FormatTimeOptions = {}): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: opts.timeZone,
  });

  return formatter.format(date);
}

/**
 * Strips the time and Z from a UTC ISO string so that new Date(...) parses it
 * as local midnight. Prevents date-shift when feeding a stored UTC date into a DatePicker.
 * Example: "2026-02-05T00:00:00.000Z" -> "2026-02-05T00:00:00"
 */
export function toLocalDateValue(isoString: string): string | null {
  return isoString ? isoString.slice(0, 10) + 'T00:00:00' : null;
}

/**
 * Formats an ISO timestamp to "MM/DD/YYYY H:MM AM/PM" format using local timezone.
 * Example: "2026-04-24T08:00:00.000Z" -> "04/24/2026 8:00 AM"
 */
export function formatIsoToDateTimeSlash(isoString: string, opts: FormatTimeOptions = {}): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  const { timeZone } = opts;

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  });

  return formatter.format(date);
}

/**
 * Converts a duration in minutes to a human-readable format.
 * Formats as "Xh Ym", "Xh", or "Ym" depending on the duration.
 *
 * Examples:
 * - 90 -> "1h 30m"
 * - 60 -> "1h"
 * - 45 -> "45m"
 * - 0 -> ""
 */
export function formatMinutesToDuration(totalMinutes: number): string {
  if (!totalMinutes) return '';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/**
 * Parses a duration string into total minutes.
 * Supports multiple formats:
 * - "1h 30m" or "1h30m" (hours and minutes)
 * - "1h30m0s" (hours, minutes, and seconds - seconds are rounded to nearest minute)
 * - "2h" (hours only)
 * - "45m" (minutes only)
 * - "1:30" (HH:MM format)
 * - "90" (plain number treated as minutes)
 *
 * Examples:
 * - "1h 30m" -> 90
 * - "1h30m0s" -> 90
 * - "2h" -> 120
 * - "45m" -> 45
 * - "1:30" -> 90
 * - "90" -> 90
 * - "" -> 0
 */
export function parseDurationToMinutes(str: string): number {
  const s = str.toLowerCase().trim();
  if (!s) return 0;

  // Try HH:MM format first
  if (/^\d+:\d+$/.test(s)) {
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
  }

  let total = 0;
  const hours = s.match(/(\d+)\s*h/);
  const minutes = s.match(/(\d+)\s*m/);
  const seconds = s.match(/(\d+)\s*s/);

  if (hours) total += parseInt(hours[1]) * 60;
  if (minutes) total += parseInt(minutes[1]);
  if (seconds) total += Math.round(parseInt(seconds[1]) / 60);

  // If no units and pure number, treat as minutes
  if (!hours && !minutes && !seconds && /^\d+$/.test(s)) {
    total = parseInt(s);
  }

  return total;
}
